import { Alert, Box, IconButton, Link, Tooltip, TooltipProps, Typography, styled, tooltipClasses } from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: 'rgba(0, 0, 0, 0)',
      maxWidth: 'none',
      fontSize: theme.typography.pxToRem(12),
    },
  }),
);

interface ChartHelpTooltipProps {
  chartType: string;
  type?: string;
  selectedValue?: string;
}

const ChartHelpTooltip = ({ chartType, type = 'chart' }: ChartHelpTooltipProps) => {
  const tooltips = {
    globalIndexRatio: (
      <Typography>
        The Global soiling index (GSI) estimates the risk of significant exposure to soiling losses from 0% to 100%. There are 4 levels in
        the GSI that would be correlated with the average soiling level.
      </Typography>
    ),
    soilingRisk: (
      <>
        <Typography>
          <b>Deposition Score</b>: Measures the impact of wind speed together with relative humidity in the velocity of soiling accumulation
          during dry season.
        </Typography>
        <Typography>
          <b>Rain Score</b>: Measures the historical periods without rainfall (i.e. periods of accumulation of soiling) over the last 10
          years.
        </Typography>
      </>
    ),
    soilingRatio: (
      <Typography>
        This curve is estimated using the total mass of airborne particles accumulated on surfaces through a typical year
      </Typography>
    ),
    externalRainPattern: (
      <>
        <Typography>
          A query is made to the NASA POWER LARC services to obtain meteorological data for the years 2020 to 2024. The parameters
          downloaded include rain (PRECTOTCORR), wind speed at 2 meters (WS2M), and relative humidity at 2 meters elevation (RH2M). This
          data is then used to generate the Typical Meteorological Year (TMY) for meteorological conditions.
        </Typography>
        <Typography>
          <Link href="https://power.larc.nasa.gov/data-access-viewer/ ">https://power.larc.nasa.gov/data-access-viewer/</Link>
        </Typography>
      </>
    ),
    externalRainPattern2: (
      <>
        <Typography>
          Upload the <b>total daily precipitation</b> in <b>mm/day</b>.
        </Typography>
        <Typography>
          ⚠️Use the format <b>"yyyy-mm-dd"</b> for the first column to indicate the timestamp.
        </Typography>
        <Typography>
          ⚠️If your data has a finer granularity, please <b>aggregate it to daily values</b> before uploading.
        </Typography>
        <Typography>
          Any data gaps during periods when soiling ratio measurements are available will be automatically filled using{' '}
          <b>NASA POWER LARC</b> data for the parameter <b>PRECTOTCORR</b>.
        </Typography>
        <Typography>
          <Link href="https://power.larc.nasa.gov/data-access-viewer/ ">https://power.larc.nasa.gov/data-access-viewer/</Link>
        </Typography>
      </>
    ),
    externalWindSpeed: (
      <>
        <Typography>
          A query is made to the NASA POWER LARC services to obtain meteorological data for the years 2020 to 2024. The parameters
          downloaded include rain (PRECTOTCORR), wind speed at 2 meters (WS2M), and relative humidity at 2 meters elevation (RH2M). This
          data is then used to generate the Typical Meteorological Year (TMY) for meteorological conditions.
        </Typography>
        <Typography>
          <Link href="https://power.larc.nasa.gov/data-access-viewer/ ">https://power.larc.nasa.gov/data-access-viewer/</Link>
        </Typography>
      </>
    ),
    externalWindSpeed2: (
      <>
        <Typography>
          Upload the <b>average daily wind speed</b> in <b>m/s</b>.
        </Typography>
        <Typography>
          ⚠️Use the format <b>"yyyy-mm-dd"</b> for the first column to indicate the timestamp.
        </Typography>
        <Typography>
          ⚠️If your data has a finer granularity, please <b>aggregate it to daily values</b> before uploading.
        </Typography>
        <Typography>
          Any data gaps during periods when soiling ratio measurements are available will be automatically filled using{' '}
          <b>NASA POWER LARC</b> data for the parameter <b>WS2M</b>.
        </Typography>
        <Typography>
          <Link href="https://power.larc.nasa.gov/data-access-viewer/ ">https://power.larc.nasa.gov/data-access-viewer/</Link>
        </Typography>
      </>
    ),
    externalRelativeHumidity: (
      <>
        <Typography>
          A query is made to the NASA POWER LARC services to obtain meteorological data for the years 2020 to 2024. The parameters
          downloaded include rain (PRECTOTCORR), wind speed at 2 meters (WS2M), and relative humidity at 2 meters elevation (RH2M). This
          data is then used to generate the Typical Meteorological Year (TMY) for meteorological conditions.
        </Typography>
        <Typography>
          <Link href="https://power.larc.nasa.gov/data-access-viewer/ ">https://power.larc.nasa.gov/data-access-viewer/</Link>
        </Typography>
      </>
    ),
    externalRelativeHumidity2: (
      <>
        <Typography>
          Upload the <b>average daily relative humidity</b> in <b>%</b>.
        </Typography>
        <Typography>
          ⚠️Use the format <b>"yyyy-mm-dd"</b> for the first column to indicate the timestamp.
        </Typography>
        <Typography>
          ⚠️If your data has a finer granularity, please <b>aggregate it to daily values</b> before uploading.
        </Typography>
        <Typography>
          Any data gaps during periods when soiling ratio measurements are available will be automatically filled using{' '}
          <b>NASA POWER LARC</b> data for the parameter <b>RH2M</b>.
        </Typography>
        <Typography>
          <Link href="https://power.larc.nasa.gov/data-access-viewer/ ">https://power.larc.nasa.gov/data-access-viewer/</Link>
        </Typography>
      </>
    ),
    externalSoilingRatio: (
      <>
        <Typography>
          The soiling ratio is calculated using NASA services that provide surface particulate matter concentrations (PM10 and PM2.5). Data
          is sourced from the MERRA-2 reanalysis database, which includes various aerosol parameters processed to derive PM10 and PM2.5
          concentrations, following the methodology described in the NASA forum:{' '}
          <Link href="https://gmao.gsfc.nasa.gov/reanalysis/MERRA-2/FAQ/">MERRA-2</Link> <br />
          To build the PM10 and PM2.5 time series, five years of data (2020–2024) are downloaded. This information is then used to generate
          a Typical Meteorological Year (TMY) for PM10, which feeds into the HSU model—together with rain patterns and module tilt angle—to
          estimate the soiling buildup over time. The resulting soiling ratio time series represents the output of the HSU model, assuming
          no human-initiated cleaning events are performed.
        </Typography>
      </>
    ),
    userSoilingRatio: (
      <>
        <Typography>
          This option is recommended if measurements from an on-site soiling sensor are available. Based on the data, the daily soiling rate
          (%/day) is calculated when the soiling ratio decreases over time. Recovery events due to rain or manual cleaning are detected and
          removed. Subsequently, the TMY rain pattern (auto-generated or user-provided) is applied to generate the typical soiling year.
        </Typography>
      </>
    ),
    soilingRatioPatternHelp: (
      <>
        <Typography>
          The daily soiling ratios are analyzed in segments to identify periods with a consistent degradation rate. Once these stable
          degradation periods are determined, the soiling ratio is recalculated using those rates, resulting in a smoother and more
          representative curve.
        </Typography>
      </>
    ),
    combineSoilingCurvesExPost: (
      <>
        <Typography>
          Select the curves that best represent the soiling status of your plant from the dropdown menu. All selected curves will be
          averaged to create a single representative curve.
        </Typography>
      </>
    ),
    combineSoilingCurvesOperational: (
      <>
        <Typography>
          Select the curves that <b>best represent the soiling status</b> of your plant from the dropdown menu. All selected curves will be{' '}
          <b>averaged to create a single representative curve</b>.
          <br />
          Please be aware that the Operational Strategy requires complete years to run. If incomplete years are provided, they will be
          completed with the HSU method for the Soiling Ratio curve and NASA POWER LARC data for the meteorological profiles.
        </Typography>
      </>
    ),
    specialCleaningEventsMitigation: (
      <>
        <Typography>Scheduled cleanings that are already planned and confirmed to take place in the plant.</Typography>
        <Typography>The selected year is ignored. Special cleaning events are supposed to be recurring every year.</Typography>
      </>
    ),
    specialCleaningEventsOperational: (
      <>
        <Typography>
          Scheduled cleanings that are already planned and confirmed to take place in the plant. The combined soiling ratio pattern is
          converted to a multiyear pattern that will be repeated. This pattern starts in 1900. If your multiyear pattern is a 3-year
          pattern, then the special cleaning events encoded here must be in years 1900, 1901, or 1902.
        </Typography>
      </>
    ),
    currentCleaningDates: (
      <>
        <Typography>
          Use the format <b>"YYYY-MM-DD"</b> to indicate each cleaning date.
        </Typography>
        <Typography>⚠️ The cleaning dates must be consistent with the uploaded data.</Typography>
        <Typography>
          For example, if soiling sensor data is available between <b>2023-01-01</b> and <b>2024-02-02</b>, cleaning dates cannot fall
          outside this range.
        </Typography>
      </>
    ),
    daysToClean: (
      <>
        <Typography>
          The number of <b>consecutive days</b> required to complete the cleaning of the entire plant.
        </Typography>
      </>
    ),
    blacklistDays: (
      <>
        <Typography>
          Dates when cleaning activities should be avoided due to external constraints (e.g., maintenance, public holidays, restricted
          access).
        </Typography>
        <Typography>The selected year is ignored. Blacklist days are supposed to be recurring every year.</Typography>
      </>
    ),
    yieldSelect: (
      <>
        <Typography>
          <b>Choose how to estimate power production</b>. Select one of the following methods to estimate energy yield for soiling loss
          assessment
        </Typography>
      </>
    ),
    yieldUpload: (
      <>
        <Typography>Upload your own simulated DC power output (hourly resolution) with no soiling losses applied.</Typography>
        <Typography>
          Ensure that the data corresponds to <b>DC production</b> (PV array output) <b>free of soiling losses</b>. In <b>PVsyst</b>, this
          can be configured under:
        </Typography>
        <Typography>
          <b>Advanced Simulation</b> {'>'} <b>Output File Detail</b> {'>'} <b>Losses</b> {'>'} <b>Soiling Loss</b>, and setting the soiling
          loss value to <b>0</b>.
        </Typography>
        <br />
        <img src="/yield_upload_tooltip.png" width="560px" />
      </>
    ),
    orientation: (
      <>
        <Typography>
          Depending on the panel orientation, the <b>table length</b> is calculated as the total distance covered by the horizontally
          arranged panels without gaps.
          <i>
            <b>Small distances due to clamps or other structural fixtures are not considered.</b>
          </i>
        </Typography>
      </>
    ),
    cleaningEfficiency: (
      <>
        <Typography>Fraction of the soiling level that is removed after a cleaning event.</Typography>
      </>
    ),
    tiltAngle: (
      <>
        <Typography>Yearly average of the PV tilt angle in ° degrees.</Typography>
      </>
    ),
    rainEfficiency: (
      <>
        <Typography>
          Fraction of the soiling level that is removed after rainfall compared to the soiling level before rainfall. A value of 0.95 is
          suggested as default.
        </Typography>
      </>
    ),
    computedRainEfficiency: (
      <>
        <Typography>Fraction of the soiling level that is removed after rainfall compared to the soiling level before rainfall.</Typography>
      </>
    ),
    rainThreshold: (
      <>
        <Typography>
          Minimum amount of rainfall needed to generate a cleaning effect on the panels. A value of 2 mm/day is suggested as default.
        </Typography>
      </>
    ),
    computedRainCleaning: (
      <>
        <Typography>
          <b>
            This option is strongly recommended for environments exposed to external pollutants, such as industrial or agricultural sites
          </b>
          . If disabled, a user-specified constant value is used.
          <br />
          The soiling laying directly on the PV modules sticks more than additional dust layers that build up on top of it. A rain event
          will therefore remove a larger fraction of the total soiling if there is more soiling on the modules. The rain cleaning efficiency
          thus varies throughout the year according to the soiling level.
        </Typography>
      </>
    ),
    plantWidth: (
      <>
        <Typography>
          <b>Plant Width</b>: The average distance a cleaning tractor or robot needs to travel to complete the cleaning of a row in the
          photovoltaic plant. For simplification, the plant is modeled as a rectangle, where the shorter side represents the plant width
        </Typography>
      </>
    ),
    meanDistance: (
      <>
        <Typography>
          The mean distance between the water station (water reservoir) and the PV plant is used to estimate fuel consumption for traveling
          from the water station to the tractor's location during cleaning, and then returning to the water station (or from the tractor's
          location to the water station). If a waterless cleaning package is chosen, this cost is negligible.
        </Typography>
      </>
    ),
    plantDimensionsHelp: (
      <>
        <img src="/plant_dimensions.png" height="500px" width="760px" />
        <Typography>
          The <b>total length of one tracker</b> is calculated based on the number of tables it holds and the spacing between each table.
          This model uses the following assumptions:
          <br />
          <ol>
            <li>Each string of PV modules is supported by a single table, i.e. one table holds exactly one string.</li>
            <li>Each tracker holds three tables, with a 0.5-meter gap between each table.</li>
          </ol>
          Length of a Tracker = Tables per Tracker x (Table Length + Table Separation) - Table Separation = 3 x (Table Length + 0.5) - 0.5
          <br />
          <br />
          <b>Row Length Calculation</b>: This parameter assesses the alignment between the user-defined <b>plant width</b> and the estimated
          tracker length:
          <ul>
            <li>
              If the <b>plant width</b> is less than the length of one tracker, the <b>row length</b> is defined by the length of one
              tracker.
            </li>
            <li>
              If the <b>plant width</b> exceeds the length of one tracker, the number of trackers that can fit within the <b>plant width</b>
              is calculated, assuming a 2.5-meter gap between each consecutive tracker.
            </li>
          </ul>
        </Typography>
      </>
    ),
    HSATsystemHelp: (
      <>
        <Typography>
          The Horizontal Single Axis Tracker (HSAT) system is a type of solar tracking system that allows photovoltaic (PV) panels to follow
          the sun's movement throughout the day. <br />
          <b>Tilt angle, 𝜷</b> : This is the angle between the plane of the solar collector and the horizontal. When the panels are
          horizontal, the tilt angle is 0º. The maximum angles occur during sunrise and sunset and are constrained by the design limitations
          of the tracker. <br />
          <b>Surface Azimuth angle, 𝜸</b> :. This is the angle between the line that points straight out from a PV panel and true south,
          measured in the horizontal plane. Angles to the east are considered negative, while angles to the west are positive. The structure
          of the tracker is typically aligned along the north-south line, i.e., an azimuth of 90º.
          <br />
          <b>In this tool, an orientation of 90º azimuth is always assumed for HSAT systems.</b>
        </Typography>
        <img src="/HSAT_system.png" width="760px" />
      </>
    ),
    FixedTiltedSystemHelp: (
      <>
        <Typography>
          A stationary system with panels installed at fixed angle.
          <br />
          <b>Tilt angle, 𝜷</b> : This is the angle between the plane of the solar collector and the horizontal. If a panel is lying flat,
          then it is 0º. As you tip it up, this angle increases. It does not matter which direction the panel faces.
          <br />
          <b>Surface Azimuth angle, 𝜸</b> :.This is the angle between the line that points straight out of a PV panel and south. It is only
          measured in the horizontal plane. Angles to the east are negative. Angles to the west are positive. If a panel pointed directly
          south, this angle would be 0º; If a panel pointed directly north, this angle would be 180º.
          <br />
          <b>In this tool it will be assumed always an orientation to the south or to the north, but not intermediate angles.</b>
        </Typography>
        <img src="/fixed_tilt_system.png" width="760px" />
      </>
    ),
    StowAngleHelp: (
      <>
        <Typography>The angle at which the tracker rests when not operating or during high winds.</Typography>
      </>
    ),
    MaxTiltAngle: (
      <>
        <Typography>The maximum angle the tracker can reach during operation.</Typography>
      </>
    ),
    RowLength: (
      <>
        <Typography>Used to calculate cleaning time in order to optimize mitigation strategy.</Typography>
      </>
    ),
    TerrainPenaltyFactor: (
      <>
        <Typography>
          Values greater than 1 indicate a reduction in speed of cleaning devices due to terrain irregularities (e.g., rough or uneven
          ground). The higher the factor, the greater the slowdown (ex. 1.3 for rough terrain).
        </Typography>
      </>
    ),
    Consumables: (
      <>
        <Typography>These costs are only applied to solutions that require water or fuel.</Typography>
      </>
    ),
    MaxCleaningsPerYear: (
      <>
        <Typography>Cleanings will be simulated up to the maximum number specified in this variable.</Typography>
      </>
    ),
    MaxCleaningTimePerCleaning: (
      <>
        <Typography>
          If the total time needed for one full cleaning cycle exceeds this value, additional systems will be added until the time is within
          the specified limit.
        </Typography>
      </>
    ),
    yieldApiHelp: (
      <>
        <Typography>
          The energy yield is simulated using a Typical Meteorological Year (TMY) file generated by PVGIS for the specified geographical
          location. The TMY is created following the ISO 15927-4 standard (see doi:10.3390/atmos9020053) using hourly data from the most
          recent available period 2005–2020 in PVGIS 5.2.
          <br />
          Solar irradiance is converted to DC power output using the NREL PVWatts DC model, and subsequently to AC output using the PVWatts
          inverter model. For more details, refer to the PVWatts documentation:{' '}
          <Link href="https://pvwatts.nrel.gov/downloads/pvwattsv5.pdf">pvwattsv5.pdf</Link>
        </Typography>
      </>
    ),
    yieldCsvHelp: (
      <>
        <Typography>Upload your own simulated DC power output (hourly resolution) with no soiling losses applied.</Typography>
        <Typography>
          Ensure that the data corresponds to <b>DC production</b> (PV array output) <b>free of soiling losses</b>. In <b>PVsyst</b>, this
          can be configured under:
        </Typography>
        <Typography>
          <b>Advanced Simulation</b> {'>'} <b>Output File Detail</b> {'>'} <b>Losses</b> {'>'} <b>Soiling Loss</b>, and setting the soiling
          loss value to <b>0</b>.
        </Typography>
        <br />
        <img src="/yield_upload_tooltip.png" width="560px" />
      </>
    ),
    yieldMonthlyHelp: (
      <Typography>
        Input monthly energy injected into the grid (MWh/month) from a simulation that excludes soiling losses. Daily production will be
        derived from these values based on the estimated soiling level.{' '}
        <b>Note: Inverter clipping and grid curtailment losses are not considered in this method.</b>
      </Typography>
    ),
    sensorDataHelp: (
      <Typography>
        For soiling sensor data, you may upload data with <b>finer granularity than daily</b>. Edit the template file by filling the
        timestamps depending on the soiling sensor type.
        <br />
        <br />
        ⚠️ The first column must contain timezone-aware timestamps in ISO 8601 format (ex. <b>"YYYY-MM-DD hh:mm:ss+00:00"</b>)
        <br />
        <h3>For RDE300 Sensors</h3>
        Required Columns:
        <ul>
          <li>
            <b>IRR1:</b> Irradiance measured by the PV cell.
          </li>
          <li>
            <b>ISC2:</b> Short-circuit current measured from the PV panel.
          </li>
          <li>
            <b>PVTEMP1:</b> Temperature of the PV cell.
          </li>
          <li>
            <b>PVTEMP2:</b> Temperature of the PV panel.
          </li>
          <li>
            <b>LastWashVol:</b> The measured short-circuit current (Isc) of the PV panel after the last wash.
          </li>
          <li>
            <b>PMAX2:</b> The measured maximum power (Pmax) of the PV panel.
          </li>
        </ul>
        For more information, please consult Section 2.3 Register Table of the{' '}
        <b>User's Guide – Soiling & Irradiance Measurement Using the RDE300 Series</b>:
        <br />
        <Link href="https://support.atonometrics.com/support/solutions/articles/71000000149-rde300-series-user-guide">
          https://support.atonometrics.com/support/solutions/articles/71000000149-rde300-series-user-guide.
        </Link>
        <h3>For DustIQ Sensors</h3>
        Required Columns:
        <ul>
          <li>
            <b>SR1:</b> Optical soiling loss detected by lens 1
          </li>
          <li>
            <b>SR2:</b> Optical soiling loss detected by lens 2
          </li>
        </ul>
        <h3>For external soiling ratio data</h3>
        Each column is supposed to be a soiling ratio computed externally (from RDE300, DustIQ or another device or methodology not included
        in this digital tool)
      </Typography>
    ),
    iscTempCoeff: (
      <Typography>
        This value can be found in the PV module's electrical specifications. Sometimes it is expressed as a percentage; in that case,
        divide it by 100. It is always a positive value.
      </Typography>
    ),
    pmaxTempCoeff: (
      <Typography>
        This value can be found in the PV module's electrical specifications. Sometimes it is expressed as a percentage; in that case,
        divide it by 100. It is always a negative value.
      </Typography>
    ),
    discount_rate: (
      <>
        <Typography>
          The interest rate used to calculate the present value of future cash flows, accounting for the time value of money and risk (8% is
          a standard value).
        </Typography>
      </>
    ),
    market_price: (
      <>
        <Typography>
          The price of the generated electricity on site - this value will be used to calculate the avoided losses made thanks to cleaning.
        </Typography>
      </>
    ),
    yearlySoilingLoss: (
      <>
        <Typography>
          Value given is the area between the production curve without soiling and the production curve with the selected cleaning package
          and soiling.
        </Typography>
      </>
    ),
    yearlyCleaningGains: (
      <>
        <Typography>
          Value given is the area between the production curve with the selected cleaning package and soiling and the production curve with
          soiling and without cleaning packages.
        </Typography>
      </>
    ),
    averageYearlySoilingLoss: (
      <>
        <Typography>
          Value given is the area between the production curve without soiling and the production curve with the selected cleaning package
          and soiling, averaged per year.
        </Typography>
      </>
    ),
    averageYearlyCleaningGains: (
      <>
        <Typography>
          Value given is the area between the production curve without soiling and the production curve with the selected cleaning package
          and soiling, averaged per year.
        </Typography>
      </>
    ),
  };

  return type === 'chart' ? (
    <Box component="span" sx={{ display: 'inline-flex' }}>
      <HtmlTooltip
        title={<Alert severity="info">{(tooltips as any)[chartType]}</Alert>}
        placement="bottom"
        sx={{ maxWidth: '1000px', zIndex: 9000 }}
      >
        <IconButton
          aria-label="help"
          disableRipple
          size="small"
          sx={{
            padding: 0,
            pointerEvents: 'auto',
            color: 'inherit',
            cursor: 'default',
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'inherit',
            },
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <HelpOutlineOutlinedIcon sx={{ marginLeft: '5px', height: '1rem' }} />
        </IconButton>
      </HtmlTooltip>
    </Box>
  ) : (
    <HtmlTooltip
      title={<Alert severity="info">{(tooltips as any)[chartType]}</Alert>}
      placement="bottom"
      sx={{ maxWidth: '1000px', zIndex: 9000 }}
    >
      <Box
        component="span"
        aria-label="help"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: 0,
          color: 'inherit',
          cursor: 'default',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <HelpOutlineOutlinedIcon sx={{ height: '1rem' }} />
      </Box>
    </HtmlTooltip>
  );
};

export default ChartHelpTooltip;
